<template>
    <div>
        <div v-if="cardMode" class="card bg-base-100 border border-base-200">
            <button class="card-body p-4 flex-row items-center justify-between text-left" type="button" :disabled="isLoading" @click="open()">
                <span><strong>{{ $tt('批量操作') }}</strong><small class="block text-base-content/60">{{ $tt('使用与快捷指令相同的完整表单') }}</small></span>
                <span class="text-lg">+</span>
            </button>
        </div>
        <button v-else-if="showTrigger" class="btn btn-sm btn-ghost drawer-button" :class="{ tooltip: !detail }" :data-tip="$tt('批量操作')" type="button" :disabled="isLoading" @click="open()">
            <span class="flex items-center" :class="{ 'w-40': detail }"><span class="w-16 text-center text-lg">+</span><span v-if="detail" class="mr-auto">{{ $tt('批量操作') }}</span></span>
        </button>

        <div class="drawer drawer-end">
            <input id="batch-operation-runner" v-model="visible" type="checkbox" class="drawer-toggle" />
            <div class="drawer-side z-50">
                <label for="batch-operation-runner" class="drawer-overlay" @click="visible = false"></label>
                <div class="batch-operation-runner">
                    <header class="runner-header">
                        <div><h2>{{ $tt(operationTitle) }}</h2><p>{{ $tt(operationDescription) }}</p></div>
                        <button class="btn btn-ghost btn-sm btn-circle" type="button" @click="visible = false">×</button>
                    </header>
                    <main class="runner-body">
                        <OperationStepForm :step="step" :show-runtime-controls="false" />
                    </main>
                    <footer class="runner-footer">
                        <button class="btn btn-outline btn-sm" type="button" @click="visible = false">{{ $tt('取消') }}</button>
                        <button class="btn btn-primary btn-sm" type="button" :disabled="submitting || !hasSelection" @click="submit">
                            <span v-if="submitting" class="loading loading-spinner loading-xs"></span>{{ $tt('确认执行') }}
                        </button>
                    </footer>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import OperationStepForm from './OperationStepForm.vue'
import { BATCH_OPERATION_CATALOG, TAG_FIELDS, UPDATE_TAG_ACTIONS, DEFAULT_TIDY_RULE_FORM, createOperationParams } from './batchOperationCatalog'

const OPERATION_CACHE_PREFIX = 'batch_operation_params_'
const TIDY_FOLDER_CACHE_KEY = `${OPERATION_CACHE_PREFIX}tidy_folder`

function clone(value) {
    return JSON.parse(JSON.stringify(value))
}

function readTidyFolderCache() {
    try {
        const cached = JSON.parse(localStorage.getItem(TIDY_FOLDER_CACHE_KEY) || 'null')
        const legacyDirList = JSON.parse(localStorage.getItem('tidy_dir_list') || 'null')
        return {
            root_path: cached?.root_path ?? localStorage.getItem('tidy_root_path') ?? '',
            dir_list: cached?.dir_list || legacyDirList,
            rule_form: cached?.rule_form
        }
    } catch (error) {
        return {}
    }
}

function readOperationCache(operation) {
    try {
        const cached = JSON.parse(localStorage.getItem(`${OPERATION_CACHE_PREFIX}${operation}`) || 'null')
        return cached && typeof cached === 'object' ? cached : null
    } catch (error) {
        return null
    }
}

const templateParams = () => ({
    template: '',
    template_mode: 'builder',
    template_parts: [{ type: 'field', name: 'tracknumber', format: '02d' }, { type: 'text', value: '. ' }, { type: 'field', name: 'artist', format: '' }, { type: 'text', value: ' - ' }, { type: 'field', name: 'title', format: '' }],
    new_template_text: '',
    template_runtime_key: '',
    empty_value_policy: 'omit_segment',
    collision_policy: 'skip'
})

export default {
    name: 'BatchOperationRunner',
    components: { OperationStepForm },
    props: {
        detail: { type: Boolean, default: false },
        isLoading: { type: Boolean, default: false },
        checkedData: { type: Array, default: () => [] },
        filePath: { type: String, default: '' },
        cardMode: { type: Boolean, default: false },
        showTrigger: { type: Boolean, default: true }
    },
    errorCaptured(error, vm, info) {
        console.error('[BatchOperationRunner] 批量操作表单渲染失败', {
            error,
            info,
            operation: this.step && this.step.operation,
            params: this.step && this.step.params
        })
        return false
    },
    data() {
        return { visible: false, submitting: false, operations: BATCH_OPERATION_CATALOG, step: this.createStep('update_tags') }
    },
    computed: {
        hasSelection() { return this.checkedData.length > 0 },
        operationDefinition() {
            return this.operations.find(operation => operation.id === this.step.operation)
        },
        operationTitle() {
            return this.operationDefinition?.display_name || '批量操作'
        },
        operationDescription() {
            return this.operationDefinition?.description || '使用与快捷指令相同的完整表单'
        }
    },
    watch: {
        step: {
            deep: true,
            handler(step) {
                this.saveOperationCache(step)
            }
        }
    },
    methods: {
        createStep(operation) {
            const params = operation === 'rename_file' ? templateParams() : createOperationParams(operation)
            if (operation === 'update_tags') {
                params.fields = TAG_FIELDS.reduce((fields, field) => {
                    fields[field.id] = { enabled: false, value: '', runtime_key: '' }
                    return fields
                }, {})
                UPDATE_TAG_ACTIONS.forEach(action => {
                    params.fields[action.id] = { enabled: false, value: true, runtime_key: '' }
                })
            }
            if (operation === 'replace_text') params.replace_text_list = [{ before: '', after: '' }]
            if (operation === 'split_filename') { params.rule_list_text = '' }
            if (operation === 'tidy_folder') {
                const cached = readTidyFolderCache()
                if (cached.root_path !== undefined) params.root_path = cached.root_path
                if (Array.isArray(cached.dir_list) && cached.dir_list.length) params.dir_list = cached.dir_list
                if (cached.rule_form && typeof cached.rule_form === 'object') {
                    params.rule_form = { ...clone(DEFAULT_TIDY_RULE_FORM), ...cached.rule_form }
                }
            }
            const cached = readOperationCache(operation)
            if (cached) Object.keys(cached).forEach(key => {
                if (cached[key] !== undefined) params[key] = cached[key]
            })
            return { operation, params }
        },
        saveTidyFolderCache(params) {
            if (!params) return
            try {
                const cache = {
                    root_path: params.root_path || '',
                    dir_list: clone(params.dir_list || []),
                    rule_form: clone({ ...DEFAULT_TIDY_RULE_FORM, ...(params.rule_form || {}) })
                }
                localStorage.setItem(TIDY_FOLDER_CACHE_KEY, JSON.stringify(cache))
                // Keep the legacy keys so the old file-operation drawer sees the same values.
                localStorage.setItem('tidy_root_path', cache.root_path)
                localStorage.setItem('tidy_dir_list', JSON.stringify(cache.dir_list))
            } catch (error) {
                // Browser storage may be unavailable or full; form operation should continue.
            }
        },
        saveOperationCache(step) {
            if (!step || !step.operation || !step.params) return
            try {
                localStorage.setItem(`${OPERATION_CACHE_PREFIX}${step.operation}`, JSON.stringify(clone(step.params)))
                if (step.operation === 'tidy_folder') this.saveTidyFolderCache(step.params)
            } catch (error) {
                // Browser storage may be unavailable or full; form operation should continue.
            }
        },
        open(operation = 'update_tags') {
            if (!this.hasSelection) return this.notify('发生提示', '请先勾选文件或文件夹！', 'warning')
            this.step = this.createStep(operation)
            this.visible = true
        },
        openOperationDrawer(operation) { this.open(operation) },
        closeOperationDrawer() { this.visible = false },
        isOperationDrawerOpen() { return this.visible },
        resetStep() { this.step = this.createStep(this.step.operation) },
        normalizeTagFields(fields) {
            const result = {}
            Object.keys(fields || {}).forEach(key => {
                const field = fields[key]
                if (field && field.enabled) result[key] = field.runtime_key ? { runtime_key: field.runtime_key } : field.value
            })
            return result
        },
        serializeParams() {
            const params = JSON.parse(JSON.stringify(this.step.params))
            if (this.step.operation === 'update_tags') {
                params.music_info = this.normalizeTagFields(params.fields)
                delete params.fields
            } else if (this.step.operation === 'auto_tag') {
                params.select_mode = params.mode
                delete params.mode
            } else if (this.step.operation === 'tidy_folder') {
                params.dir_list = (params.dir_list || []).map(item => ({ value: item.value, value2: item.value2 || '' }))
                delete params.rule_form_text
            } else if (this.step.operation === 'split_filename') {
                params.rule_list = (params.rule_list_text || '').split('\n').map(item => item.trim()).filter(Boolean)
                delete params.rule_list_text
            } else if (this.step.operation === 'replace_text') {
                params.replace_mode = params.mode
                params.modify_list = params.fields
                delete params.mode; delete params.fields
            } else if (this.step.operation === 'convert_chinese' || this.step.operation === 'encoding_fix') {
                params.modify_list = params.fields
                delete params.fields
            } else if (this.step.operation === 'replay_gain') {
                params.is_regainer = true
                delete params.mode
            }
            return params
        },
        async submit() {
            this.submitting = true
            this.$emit('loading-start')
            try {
                const params = this.serializeParams()
                const payload = { file_full_path: this.filePath, select_data: this.checkedData, ...params }
                const api = this.$api.Task
                const calls = {
                    rename_file: () => api.renameFiles({
                        ...payload,
                        template: this.step.params.template_mode === 'advanced'
                            ? this.step.params.template
                            : this.step.params.template_parts,
                    }),
                    update_tags: () => api.batchUpdateId3({ ...payload, music_info: params.music_info, is_regainer: false }),
                    auto_tag: () => api.batchAutoUpdateId3({ ...payload, music_info: params, modify_list: params.modify_list }),
                    background_scrape: () => api.batchBackgroundScrape(payload),
                    tidy_folder: () => api.tidyFolder(payload),
                    remove_null_folder: () => api.removeNullFolder(payload),
                    format_music: () => api.formatMusic(payload),
                    split_cue: () => api.splitCueMusic(payload),
                    import_music: () => api.importMusicIn({ ...payload, music_info: {} }),
                    duplicate_check: () => api.checkDuplicate(payload),
                    replay_gain: () => api.batchUpdateId3({ ...payload, music_info: {}, is_regainer: true }),
                    generate_strm: () => api.generateStrm(payload),
                    convert_chinese: () => api.simplifiedFields(payload),
                    split_filename: () => api.splitFilename(payload),
                    replace_text: () => api.replaceText(payload),
                    encoding_fix: () => api.fixEncoding(payload),
                    file_index: () => api.fileIndex(payload)
                }
                const response = await calls[this.step.operation]()
                if (!response || response.result === false) throw new Error(response?.message || this.$tt('创建失败'))
                this.notify('创建成功！', '任务在后台努力运行中', 'success')
                this.visible = false
                this.$parent.handleSearchFile && this.$parent.handleSearchFile()
            } catch (error) {
                this.notify('创建失败！', error.message, 'error')
            } finally {
                this.submitting = false
                this.$emit('loading-end')
            }
        },
        notify(title, content, type) { this.$myMsg.notify({ title: this.$tt(title), content: this.$tt(content), type, time: 3000 }) }
    }
}
</script>

<style scoped>
.batch-operation-runner { display: flex; width: min(100vw, 500px); min-height: 100%; flex-direction: column; background: oklch(var(--b1)); color: oklch(var(--bc)); }
.runner-header, .runner-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px; border-bottom: 1px solid oklch(var(--bc) / .1); }
.runner-header h2 { margin: 0; font-size: 20px; font-weight: 800; }.runner-header p { margin: 4px 0 0; color: oklch(var(--bc) / .58); font-size: 12px; }
.runner-body { display: grid; align-content: start; gap: 14px; flex: 1; padding: 16px; overflow-y: auto; }.runner-footer { border-top: 1px solid oklch(var(--bc) / .1); border-bottom: 0; }
.runner-body ::v-deep .operation-section__header { display: none; }

@media (max-width: 767px) {
    .drawer-side {
        position: fixed;
        inset: 0;
        width: 100vw;
        height: 100dvh !important;
        min-height: 100dvh !important;
        max-height: none !important;
        overflow: hidden;
    }

    .drawer-side > .batch-operation-runner {
        width: 100%;
        max-width: none;
        height: 100% !important;
        min-height: 100% !important;
        max-height: none !important;
        box-sizing: border-box;
        padding-bottom: env(safe-area-inset-bottom, 0px);
    }

    .runner-header {
        flex: 0 0 auto;
        padding-top: calc(12px + env(safe-area-inset-top, 0px));
    }

    .runner-body {
        min-height: 0;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
    }

    .runner-footer {
        flex: 0 0 auto;
        padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
    }
}
</style>
